import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ativosMeta, type Coleta, type ContaAdmin } from '../api/admin'
import { pedidoAoDono, PLATAFORMAS_ATIVACAO, type InfoAtivacao, type PlataformaAtivacao } from '../utils/ativacao'

/**
 * A ativação de uma marca, plataforma por plataforma: o que já está conectado, o botão para
 * escolher a conta (com o "Buscar") e o pedido pronto para mandar ao dono da marca.
 */
export function AtivacaoMarca({
  marca,
  contas,
  coletas,
  info,
  onConectar,
}: {
  marca: string
  contas: ContaAdmin[]
  /** A última coleta de cada conta: só conta como conectada a que já coletou. */
  coletas: Map<string, Coleta>
  info: InfoAtivacao
  onConectar: (plataforma: PlataformaAtivacao) => void
}) {
  const [pedido, setPedido] = useState(false)
  const reais = (p: PlataformaAtivacao) => contas.filter((c) => c.plataforma === p && c.ativa && !c.demo)
  // Conectada = cadastrada E coletou. Cadastrada sem coleta boa = o acesso ainda não chegou.
  const conectadas = (p: PlataformaAtivacao) => reais(p).filter((c) => coletas.get(c.id)?.status === 'ok').length
  const faltando = PLATAFORMAS_ATIVACAO.filter((p) => conectadas(p.id) === 0).map((p) => p.id)

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Box sx={{ mr: 'auto', maxWidth: 640 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
            Ativação da marca
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Para cada plataforma: o dono da marca libera o acesso, você escolhe a conta em "Conectar" e o sistema faz a primeira coleta.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" disabled={!faltando.length} onClick={() => setPedido(true)} sx={{ textTransform: 'none' }}>
          Pedir acesso ao dono da marca
        </Button>
      </Stack>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        {PLATAFORMAS_ATIVACAO.map((p) => {
          const n = conectadas(p.id)
          const pendentes = reais(p.id).length - n
          return (
            <Stack
              key={p.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, minWidth: 0 }}
            >
              <Box sx={{ mr: 'auto', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {p.nome}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={n ? 'success' : pendentes ? 'warning' : 'default'}
                  label={n ? (n === 1 ? 'Conectada' : `${n} contas`) : pendentes ? 'Sem coleta: confira o acesso' : 'Não conectada'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Button size="small" onClick={() => onConectar(p.id)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                {n || pendentes ? 'Adicionar outra' : 'Conectar'}
              </Button>
            </Stack>
          )
        })}
      </Box>
      {pedido ? <PedidoAoDono marca={marca} faltando={faltando} info={info} onFechar={() => setPedido(false)} /> : null}
    </Paper>
  )
}

/** O pedido de acesso, pronto para copiar e mandar ao dono da marca (e-mail, WhatsApp). */
function PedidoAoDono({ marca, faltando, info, onFechar }: { marca: string; faltando: PlataformaAtivacao[]; info: InfoAtivacao; onFechar: () => void }) {
  const [escolhidas, setEscolhidas] = useState<Set<PlataformaAtivacao>>(new Set(faltando))
  const [portfolio, setPortfolio] = useState(info.portfolioMeta)
  const precisaPortfolio = !info.portfolioMeta && faltando.some((p) => ['meta_ads', 'instagram', 'facebook'].includes(p))
  const [buscandoMeta, setBuscandoMeta] = useState(precisaPortfolio)
  const [copiado, setCopiado] = useState(false)

  // O ID do portfólio vem do Meta (é com ele que o dono compartilha); busca uma vez ao abrir.
  useEffect(() => {
    if (!precisaPortfolio) return
    let cancelado = false
    ativosMeta()
      .then((a) => !cancelado && setPortfolio(a.portfolios?.[0] ?? null))
      .catch(() => !cancelado && setPortfolio(null))
      .finally(() => !cancelado && setBuscandoMeta(false))
    return () => {
      cancelado = true
    }
  }, [precisaPortfolio])

  const ordem = PLATAFORMAS_ATIVACAO.map((p) => p.id).filter((p) => escolhidas.has(p))
  const texto = pedidoAoDono(marca, ordem, { ...info, portfolioMeta: portfolio })

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
    } catch {
      setCopiado(false)
    }
  }

  return (
    <Dialog open onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Pedir acesso ao dono da {marca}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Marque o que pedir e copie o texto para mandar a quem administra as contas da marca. Quando ele liberar, use "Conectar" em cada
            plataforma.
          </Typography>
          <Box>
            {PLATAFORMAS_ATIVACAO.map((p) => (
              <FormControlLabel
                key={p.id}
                control={
                  <Checkbox
                    size="small"
                    checked={escolhidas.has(p.id)}
                    onChange={(e) => {
                      const n = new Set(escolhidas)
                      if (e.target.checked) n.add(p.id)
                      else n.delete(p.id)
                      setEscolhidas(n)
                      setCopiado(false)
                    }}
                  />
                }
                label={p.nome}
              />
            ))}
          </Box>
          {buscandoMeta ? <LinearProgress /> : null}
          <TextField value={texto} multiline minRows={8} slotProps={{ input: { readOnly: true } }} />
          {copiado ? <Alert severity="success">Copiado. Cole no e-mail ou no WhatsApp para o dono da marca.</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        <Button variant="contained" disabled={!texto || buscandoMeta} onClick={copiar} sx={{ textTransform: 'none' }}>
          Copiar texto
        </Button>
      </DialogActions>
    </Dialog>
  )
}
